import React from 'react';
import type {
  DeregisterValidationFn,
  FormBlurEventFn,
  HasValidationFn,
  RegisterValidationFn,
  SetFormValueFn,
} from '@noths/polaris-client-react-hooks';
import {
  CloseButton as DeleteButton,
  TextButton as AddButton,
} from '@noths/polaris-client-ribbons-design-system';
import { v4 as uuidv4 } from 'uuid';

import { TextInput } from 'src/components/atoms/TextInput/TextInput';
import { WeblinkInput } from 'src/components/atoms/WeblinkInput/WeblinkInput';
import { WEBLINK_MUST_BE_PROVIDED_ERROR } from 'src/components/organisms/Signup/constants/copy';
import { PRODUCT_WEBLINK_PREFIX_TESTID } from 'src/components/organisms/Signup/constants/testIds';
import type { FormValidationErrors } from 'src/components/organisms/Signup/types';
import * as styles from 'src/styles/styles';
import type { Product } from 'src/types/product';
import type { RegistrationForm } from 'src/types/registration';

interface Props {
  'data-position': string;
  'data-section': string;
  deregisterValidation: DeregisterValidationFn;
  errors: FormValidationErrors;
  formValues: RegistrationForm;
  hasValidation: HasValidationFn;
  onBlur: FormBlurEventFn;
  registerValidation: RegisterValidationFn<RegistrationForm>;
  setError: (errorName: string, errorMessage: string) => void;
  setFormValue: SetFormValueFn<RegistrationForm>;
}

const getDynamicProductFieldName = (id: string) => `products.${id}.url`;

export const Products = ({
  'data-position': dataPosition,
  'data-section': dataSection,
  deregisterValidation,
  errors,
  formValues,
  hasValidation,
  onBlur,
  registerValidation,
  setError,
  setFormValue,
}: Props) => {
  const TRACKING_POSITION_OFFSET = 1;

  const registerProductValidation = (id: string) => {
    const associatedUrlInputFieldName = getDynamicProductFieldName(id);

    registerValidation(associatedUrlInputFieldName, {
      validate: {
        value: (_val, state) => {
          const product = state.products.find((product) => product.id === id);

          /* istanbul ignore next */
          if (product && product.productName.trim() !== '') {
            return product.url.trim() === '';
          }

          /* istanbul ignore next */
          return false;
        },
        message: WEBLINK_MUST_BE_PROVIDED_ERROR,
      },
    });
  };

  const handleDeleteProduct = (id: string) => {
    const associatedUrlInputFieldName = getDynamicProductFieldName(id);

    if (hasValidation(associatedUrlInputFieldName)) {
      deregisterValidation(associatedUrlInputFieldName);
    }

    const updatedProducts = formValues.products.filter((product) => product.id !== id);

    setFormValue('products', updatedProducts);
  };
  return (
    <>
      <p css={styles.label}>
        Showcase your best products you&apos;re interested in selling with us
      </p>
      <div css={styles.productTextFields}>
        {formValues.products.map(({ id, productName: name, url }: Product, index: number) => (
          <div key={id}>
            <p css={styles.label}>{`Product ${index + 1}`}</p>
            <div css={styles.removableProductTextField}>
              <TextInput
                css={styles.textFieldLabel}
                data-position={`${dataPosition}.${index + 1}.1`}
                data-section={dataSection}
                id={`company-product-productName-${id}`}
                label={`Product ${id}`}
                labelHidden
                name={`products.${id}.productName`}
                onChange={({ target }) => {
                  const { value } = target;
                  const productName = value.trim();

                  const associatedUrlInputFieldName = getDynamicProductFieldName(id);
                  const hasUrlFieldvalidation = hasValidation(associatedUrlInputFieldName);

                  if (!hasUrlFieldvalidation) {
                    registerProductValidation(id);
                  }

                  if (hasUrlFieldvalidation && !productName.length) {
                    deregisterValidation(associatedUrlInputFieldName);
                  }

                  const products = [...formValues.products];
                  const productNdx = products.findIndex((product) => product.id === id);
                  const product = products[productNdx];
                  products[productNdx] = { ...product, productName };

                  setFormValue('products', products);
                }}
                placeholder="Product name"
                value={name}
              />
              <WeblinkInput
                css={styles.textFieldLabel}
                data-position={`${dataPosition}.${index + TRACKING_POSITION_OFFSET}.2`}
                data-section={dataSection}
                data-testid={`${PRODUCT_WEBLINK_PREFIX_TESTID}-${id}`}
                disabled={!name}
                error={errors[getDynamicProductFieldName(id)] as string | undefined}
                id={`company-product-weblink-${id}`}
                label=""
                labelHidden
                name={getDynamicProductFieldName(id)}
                onBlur={onBlur}
                onChange={({ target }) => {
                  const { value } = target;
                  const url = value.trim();

                  const products = [...formValues.products];
                  const productNdx = products.findIndex((product) => product.id === id);
                  const product = products[productNdx];
                  products[productNdx] = { ...product, url };

                  setFormValue('products', products);
                }}
                onWeblinkError={(errorMessage) =>
                  setError(getDynamicProductFieldName(id), errorMessage)
                }
                placeholder="Weblink"
                value={url}
              />
              <DeleteButton
                aria-label="Delete product"
                id={`company-product-delete-${id}`}
                onClick={() => handleDeleteProduct(id)}
                size="small"
              />
            </div>
          </div>
        ))}
      </div>
      <AddButton
        css={styles.addButton}
        id="company-product-weblink-add"
        onClick={(e) => {
          e.preventDefault();

          const products = [...formValues.products, { productName: '', url: '', id: uuidv4() }];

          setFormValue('products', products);
        }}
      >
        <span className="underlined">Add one more</span>
      </AddButton>
    </>
  );
};
